import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getErrorMessage,
  type PartFileUnits,
  useUpdatePartFile,
  PART_FILE_UNITS,
  PREFERRED_UNIT_SYSTEMS,
} from '@frontend/api';
import { useToast } from '@frontend/components';

import { viewer } from 'modules/common/viewer/data/actions';
import { getActiveFile } from 'modules/common/viewer/data/reducer';

import { useUnitSystemContext } from 'utils/measurement/context';

export const usePrintUnitsFeature = ({
  onSetPrintUnitsSuccess,
}: { onSetPrintUnitsSuccess?: () => void } = {}) => {
  const activeFile = useSelector(getActiveFile);

  const { preferredUnitSystem } = useUnitSystemContext();

  const { printUnits: printUnitsFile } = activeFile ?? {};

  const dispatch = useDispatch();
  const toast = useToast();

  const { mutate: updatePartFile } = useUpdatePartFile(
    { uuid: activeFile?.uuid ?? '' },
    {
      queryOptions: {
        onSuccess: (data) => {
          toast({
            status: 'success',
            title: 'Units updated',
          });
          if (activeFile) {
            // Run success callback if provided
            onSetPrintUnitsSuccess?.();

            // inject part file into the store with new units
            dispatch(
              viewer.receivePartFiles({
                partFiles: [
                  {
                    ...activeFile,
                    printUnits: data.printUnits,
                  },
                ],
              }),
            );
          }
        },
        onError: (error) => {
          toast({
            status: 'error',
            title: 'Failed to update units',
            description: getErrorMessage(error),
          });
        },
      },
    },
  );

  const handleUpdatePrintUnits = useCallback(
    (units: PartFileUnits) => {
      updatePartFile({ printUnits: units });
    },
    [updatePartFile],
  );

  // if print units not specified, default them to the preferred unit system
  const printUnits = useMemo(
    () =>
      printUnitsFile ??
      (preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? PART_FILE_UNITS.IN
        : PART_FILE_UNITS.MM),
    [printUnitsFile, preferredUnitSystem],
  );

  return {
    printUnits,
    handleUpdatePrintUnits,
  };
};
